import React, { useEffect, useRef, useState } from "react";
import { MdFullscreen, MdFullscreenExit } from "react-icons/md";
import JSMpeg from "@cycjimmy/jsmpeg-player";
import { Spinner, Stack } from "react-bootstrap";

export const VideoPlayer = ({ wsUrl, cameraName }) => {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const transformedWsUrl = wsUrl.replace(/ws:/, "ws/");

    useEffect(() => {
        let player;
        let reconnectTimer;

        const createPlayer = () => {
            if (canvasRef.current) {
                player = new JSMpeg.Player(transformedWsUrl, {
                    canvas: canvasRef.current,
                    pauseWhenHidden: false,
                    onVideoDecode: () => {
                        setIsLoading(false);
                    },
                    onDisconnect: () => {
                        setIsLoading(true);
                        reconnect();
                    },
                });

                const fallbackTimeout = setTimeout(() => setIsLoading(false), 5000);
                return () => clearTimeout(fallbackTimeout);
            }
        };

        const reconnect = () => {
            if (reconnectTimer) clearTimeout(reconnectTimer);
            reconnectTimer = setTimeout(() => {
                if (player) {
                    player.destroy();
                    player = null;
                }
                createPlayer();
            }, 3000);
        };

        createPlayer();

        const handleFullScreenChange = () => {
            setIsFullScreen(!!document.fullscreenElement);
        };
        document.addEventListener("fullscreenchange", handleFullScreenChange);

        return () => {
            if (player) {
                player.destroy();
                player = null;
            }
            if (reconnectTimer) clearTimeout(reconnectTimer);
            document.removeEventListener("fullscreenchange", handleFullScreenChange);
        };
    }, [transformedWsUrl]);

    
    const toggleFullScreen = () => {
        if (!isFullScreen) {
            const el = canvasRef.current || containerRef.current;
            if (el?.requestFullscreen) {
                el.requestFullscreen();
            } else if (el?.webkitRequestFullscreen) {
                el.webkitRequestFullscreen(); // Safari
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            }
        }
    };

    return (
        <div
            ref={containerRef}
            // ↓ removed bg-dark and maxWidth so video fills the card naturally
            className="position-relative w-100"
            style={{
                cursor: "pointer",
                aspectRatio: "16/9",
                minHeight: "180px",       // reserves space before stream loads
                backgroundColor: "#111", // dark placeholder so no layout jump
            }}
            onDoubleClick={toggleFullScreen}
        >
            {isLoading && (
                <Stack
                    direction="horizontal"
                    className="justify-content-center align-items-center position-absolute w-100 h-100"
                    style={{ top: 0, left: 0, zIndex: 1, backgroundColor: "rgba(0,0,0,0.4)" }}
                >
                    <Spinner animation="border" className="text-white" />
                </Stack>
            )}

            <canvas
                ref={canvasRef}
                className="position-absolute top-0 start-0 w-100 h-100 d-block object-fit-fill"
            />

            <button
                onClick={(e) => { e.stopPropagation(); toggleFullScreen(); }}
                className="position-absolute pb-1 border-0 me-1 end-0 bottom-0 bg-transparent rounded-2 text-white"
                style={{ zIndex: 2 }}
            >
                {isFullScreen ? <MdFullscreenExit size={30} /> : <MdFullscreen color="white" size={25} />}
            </button>
        </div>
    );
};